import type { CollisionBox, InteractionDef, OccluderDef, Rect } from '../types/data';

type DraftMode = 'collision' | 'interactable' | 'npc_zone' | 'occluder';

type WorldDraft = {
  collision: CollisionBox[];
  interactable: InteractionDef[];
  npc_zone: Rect[];
  occluder: OccluderDef[];
  updatedAt: string;
};

type DraftRoot = Record<string, WorldDraft>;

const STORAGE_KEY = 'jrpg-launcher:drafts:v1';

function createEmptyDraft(): WorldDraft {
  return {
    collision: [],
    interactable: [],
    npc_zone: [],
    occluder: [],
    updatedAt: new Date().toISOString(),
  };
}

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readRoot(): DraftRoot {
  if (!canUseStorage()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as DraftRoot;
  } catch {
    return {};
  }
}

function writeRoot(root: DraftRoot) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(root, null, 2));
}

export class EditorDraftStore {
  static getWorldDraft(worldKey: string): WorldDraft {
    return readRoot()[worldKey] ?? createEmptyDraft();
  }

  static getCounts(worldKey: string) {
    const draft = this.getWorldDraft(worldKey);
    return {
      collision: draft.collision.length,
      interactable: draft.interactable.length,
      npc_zone: draft.npc_zone.length,
      occluder: draft.occluder.length,
    };
  }

  static append(worldKey: string, mode: DraftMode, payload: unknown) {
    const root = readRoot();
    const draft = root[worldKey] ?? createEmptyDraft();
    (draft[mode] as unknown[]).push(payload);
    draft.updatedAt = new Date().toISOString();
    root[worldKey] = draft;
    writeRoot(root);
  }

  static clearWorld(worldKey: string) {
    const root = readRoot();
    delete root[worldKey];
    writeRoot(root);
  }

  static exportWorldJson(worldKey: string) {
    const draft = this.getWorldDraft(worldKey);
    return JSON.stringify({ worldKey, ...draft }, null, 2);
  }
}
